import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import DrawerItems from "./DrawerItems";
import NavbarNavigationItems from "./NavbarNavigationItems";
import NavbarLogo from "./NavbarLogo";
import SvgIcon from "../../helpers/SvgIcon";

const drawerWidth = 276;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  minWidth: 0,
  // padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  // justifyContent: "flex-start",
}));

export default function NavbarPersistWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(false);
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const desktopOpen = open && !isMobile;

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box className="flex h-full">
      <AppBar position="fixed" open={desktopOpen} elevation={3}>
        <Toolbar sx={{ gap: { xs: 0.5, sm: 1 }, px: { xs: 1, sm: 2 } }}>
          {!desktopOpen && (
            <IconButton
              ref={menuButtonRef}
              color="inherit"
              aria-label="Deschide meniul"
              aria-controls={open ? "main-drawer-navigation" : undefined}
              aria-expanded={open}
              onClick={handleDrawerOpen}
              sx={{ width: 44, height: 44, flexShrink: 0 }}>
              <SvgIcon
                type="MENU_ICON"
                fill="currentColor"
                className="!h-[24px] !w-[24px]"
              />
            </IconButton>
          )}
          {!desktopOpen && <NavbarLogo withEnvIndicator />}
          <NavbarNavigationItems />
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: isMobile ? 0 : drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            maxWidth: "calc(100vw - 24px)",
            boxSizing: "border-box",
            overflow: "hidden",
            borderRight: "1px solid #e8edf3",
            boxShadow: "4px 0 24px rgba(24, 28, 50, 0.06)",
          },
        }}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
        PaperProps={{
          onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Escape") handleDrawerClose();
          },
        }}
        SlideProps={{
          onEntered: () => closeButtonRef.current?.focus(),
          onExited: () => menuButtonRef.current?.focus(),
        }}>
        <DrawerHeader className="shrink-0 border-b border-slate-100">
          <div className="flex w-full items-center justify-between">
            {open && <NavbarLogo />}

            <IconButton
              ref={closeButtonRef}
              aria-label="Închide meniul"
              onClick={handleDrawerClose}
              className="!h-[44px] !w-[44px] !rounded-lg !text-muted-dark hover:!bg-surface-subtle">
              {theme.direction === "ltr" ? (
                <SvgIcon
                  type="CHEVRON_LEFT_ICON"
                  fill="currentColor"
                  className="!h-[20px] !w-[20px]"
                />
              ) : (
                <SvgIcon
                  type="CHEVRON_RIGHT_ICON"
                  fill="currentColor"
                  className="!h-[20px] !w-[20px]"
                />
              )}
            </IconButton>
          </div>
        </DrawerHeader>
        <DrawerItems
          handleDrawerClose={handleDrawerClose}
          isDrawerOpen={open}
        />
      </Drawer>
      <Main open={desktopOpen} sx={isMobile ? { marginLeft: 0 } : undefined}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
}
